import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';

export const Privacy = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check if accessed from the app with the correct parameter
  const isFromApp = searchParams.get('app') === 'holyword';

  useEffect(() => {
    // Redirect to home if not accessed from app
    if (!isFromApp) {
      navigate('/');
    }
  }, [isFromApp, navigate]);

  // Don't render anything while redirecting
  if (!isFromApp) {
    return null;
  }

  return (
    <div data-testid="privacy-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
              <Shield className="text-gold" size={32} />
            </div>
            <h1 className="font-cinzel text-4xl sm:text-5xl text-foreground mb-4">
              Privacy <span className="text-gold">Policy</span>
            </h1>
            <p className="text-muted-foreground">
              The Holy WORD Bible App
            </p>
            <p className="text-muted-foreground text-sm mt-2">
              Last Updated: April 1, 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Introduction */}
            <div className="card-dark">
              <h2 className="font-cinzel text-2xl text-gold mb-4">Introduction</h2>
              <p className="text-muted-foreground">
                True Believer Family ("we," "our," or "us") operates The Holy WORD Bible mobile application (the "App"). This Privacy Policy explains how we collect, use, and protect your information when you use our App.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="card-dark">
              <h2 className="font-cinzel text-2xl text-gold mb-4">Information We Collect</h2>
              
              <h3 className="text-foreground font-semibold mt-4 mb-2">Device Information</h3>
              <ul className="text-muted-foreground list-disc list-inside space-y-1 ml-2">
                <li>Device identifier (anonymous ID for app functionality)</li>
                <li>Device type and operating system</li>
                <li>App version</li>
              </ul>

              <h3 className="text-foreground font-semibold mt-6 mb-2">Usage Data</h3>
              <ul className="text-muted-foreground list-disc list-inside space-y-1 ml-2">
                <li>Reading history and progress</li>
                <li>Reading streaks and statistics</li>
                <li>Bookmarks, highlights, and notes you create</li>
                <li>Feature preferences and settings</li>
              </ul>

              <h3 className="text-foreground font-semibold mt-6 mb-2">Subscription Information</h3>
              <ul className="text-muted-foreground list-disc list-inside space-y-1 ml-2">
                <li>Purchase history (processed through Apple App Store or Google Play)</li>
                <li>Subscription status</li>
                <li>We do NOT have access to your payment details - these are handled securely by Apple/Google</li>
              </ul>

              <h3 className="text-foreground font-semibold mt-6 mb-2">Kids Mode</h3>
              <ul className="text-muted-foreground list-disc list-inside space-y-1 ml-2">
                <li>Game scores and achievement progress</li>
                <li>Story completion status</li>
                <li>No personal information is collected from children</li>
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div className="card-dark">
              <h2 className="font-cinzel text-2xl text-gold mb-4">How We Use Your Information</h2>
              <ul className="text-muted-foreground list-disc list-inside space-y-2 ml-2">
                <li>To provide and maintain the App's functionality</li>
                <li>To sync your reading progress across sessions</li>
                <li>To track your reading streaks and achievements</li>
                <li>To process and manage your subscription</li>
                <li>To improve the App experience</li>
                <li>To provide customer support</li>
              </ul>
            </div>

            {/* Data Storage */}
            <div className="card-dark">
              <h2 className="font-cinzel text-2xl text-gold mb-4">Data Storage</h2>
              <ul className="text-muted-foreground list-disc list-inside space-y-2 ml-2">
                <li>Your reading data is stored locally on your device</li>
                <li>Some data is synced to our secure servers to enable features like reading history</li>
                <li>We use MongoDB for secure data storage</li>
                <li>Subscription data is managed by RevenueCat, a trusted third-party subscription management platform</li>
              </ul>
            </div>

            {/* Third-Party Services */}
            <div className="card-dark">
              <h2 className="font-cinzel text-2xl text-gold mb-4">Third-Party Services</h2>
              <p className="text-muted-foreground mb-4">Our App uses the following third-party services:</p>
              <ul className="text-muted-foreground list-disc list-inside space-y-2 ml-2">
                <li><strong className="text-foreground">RevenueCat:</strong> For subscription management (<a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">RevenueCat Privacy Policy</a>)</li>
                <li><strong className="text-foreground">Apple App Store / Google Play:</strong> For payment processing</li>
                <li><strong className="text-foreground">OpenAI:</strong> For AI Study Assistant features (no personal data is shared)</li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="card-dark">
              <h2 className="font-cinzel text-2xl text-gold mb-4">Data Security</h2>
              <p className="text-muted-foreground">
                We implement appropriate security measures to protect your information. However, no method of electronic transmission or storage is 100% secure.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="card-dark">
              <h2 className="font-cinzel text-2xl text-gold mb-4">Children's Privacy</h2>
              <p className="text-muted-foreground">
                Our Kids Mode is designed for children ages 3-12. We do not knowingly collect personal information from children. Kids Mode only tracks game scores and story progress locally and anonymously.
              </p>
            </div>

            {/* Your Rights */}
            <div className="card-dark">
              <h2 className="font-cinzel text-2xl text-gold mb-4">Your Rights</h2>
              <p className="text-muted-foreground mb-4">You have the right to:</p>
              <ul className="text-muted-foreground list-disc list-inside space-y-2 ml-2">
                <li>Access your data stored in the App</li>
                <li>Delete your reading history and preferences</li>
                <li>Cancel your subscription at any time through your device settings</li>
                <li>Contact us with privacy concerns</li>
              </ul>
            </div>

            {/* Data Retention */}
            <div className="card-dark">
              <h2 className="font-cinzel text-2xl text-gold mb-4">Data Retention</h2>
              <ul className="text-muted-foreground list-disc list-inside space-y-2 ml-2">
                <li>Reading history and preferences are retained while you use the App</li>
                <li>You can clear your data at any time through the App settings</li>
                <li>Subscription records are retained as required by Apple/Google</li>
              </ul>
            </div>

            {/* Changes to This Policy */}
            <div className="card-dark">
              <h2 className="font-cinzel text-2xl text-gold mb-4">Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy in the App.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-cinzel text-2xl text-gold mb-4">Contact Us</h2>
            <p className="text-muted-foreground mb-6">
              If you have questions about this Privacy Policy, please contact us:
            </p>
            <div className="text-foreground space-y-2">
              <p className="font-cinzel text-lg">True Believer Family</p>
              <p>Email: <a href="mailto:privacy@truebelieverfam.com" className="text-gold hover:underline">privacy@truebelieverfam.com</a></p>
              <p>Website: <a href="https://truebelieverfam.com" className="text-gold hover:underline">https://truebelieverfam.com</a></p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
